import React, { useCallback, useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { fmtTRY, fmtDate, todayISO } from "@/lib/format";
import { toast } from "sonner";
import * as XLSX from "xlsx";
import {
  Plus, Trash2, X, ArrowLeft, ChevronRight, CircleDollarSign,
  AlertTriangle, CheckCircle2, Undo2, Landmark, TrendingDown, Edit3, Check, Upload, FileSpreadsheet,
} from "lucide-react";

const LOAN_TYPES = {
  rotative: "Rotatif",
  vehicle: "Taşıt",
  commercial: "Ticari",
  overdraft: "KMH",
  other: "Diğer",
};

const STATUS_COLOR = {
  pending: "text-neutral-600 border-neutral-300",
  paid: "text-emerald-700 border-emerald-300 bg-emerald-50",
  overdue: "text-rose-700 border-rose-300 bg-rose-50",
};

// Annuite formula preview (client-side echo of backend)
// ----- Excel/CSV plan parser -----
// Beklenen: 2 sütun (Vade Tarihi, Taksit Tutarı) veya 3+ sütun.
// Başlık satırı otomatik atlanır. Türkçe tutar (12.345,67 TL) ve
// çeşitli tarih formatları (Excel serial, dd.mm.yyyy, yyyy-mm-dd) desteklenir.
function _parseAmount(v) {
  if (typeof v === "number") return v;
  if (v == null) return NaN;
  const s = String(v).replace(/\s/g, "").replace(/TL|TRY|₺/gi, "").replace(/\./g, "").replace(",", ".");
  const n = Number(s);
  return isNaN(n) ? NaN : n;
}
function _parseDate(v) {
  if (v == null || v === "") return null;
  if (v instanceof Date && !isNaN(v)) return v.toISOString().slice(0, 10);
  if (typeof v === "number") {
    // Excel serial date
    const epoch = new Date(Date.UTC(1899, 11, 30));
    const d = new Date(epoch.getTime() + v * 86400000);
    if (!isNaN(d)) return d.toISOString().slice(0, 10);
  }
  const s = String(v).trim();
  // yyyy-mm-dd
  let m = /^(\d{4})-(\d{1,2})-(\d{1,2})$/.exec(s);
  if (m) return `${m[1]}-${m[2].padStart(2, "0")}-${m[3].padStart(2, "0")}`;
  // dd.mm.yyyy or dd/mm/yyyy
  m = /^(\d{1,2})[./-](\d{1,2})[./-](\d{2,4})$/.exec(s);
  if (m) {
    let yy = m[3];
    if (yy.length === 2) yy = "20" + yy;
    return `${yy}-${m[2].padStart(2, "0")}-${m[1].padStart(2, "0")}`;
  }
  const d = new Date(s);
  if (!isNaN(d)) return d.toISOString().slice(0, 10);
  return null;
}
async function parseInstallmentsFile(file) {
  const buf = await file.arrayBuffer();
  const wb = XLSX.read(buf, { cellDates: true });
  const ws = wb.Sheets[wb.SheetNames[0]];
  const rows = XLSX.utils.sheet_to_json(ws, { header: 1, defval: "" });
  const out = [];
  for (const row of rows) {
    if (!row || row.length < 2) continue;
    const date = _parseDate(row[0]);
    const amt = _parseAmount(row[1]);
    if (!date || !amt || amt <= 0) continue;
    out.push({ due_date: date, amount: Math.round(amt * 100) / 100 });
  }
  return out;
}

function annuityPreview(principal, annualRate, months, bsmvRate = 0) {
  const p = Number(principal), r = Number(annualRate) / 100 / 12, n = Number(months);
  const bsmv = (Number(bsmvRate) || 0) / 100;
  if (!p || !n) return { monthly: 0, total: 0, interest: 0, bsmv: 0, monthlyBase: 0 };
  const monthlyBase = r === 0 ? p / n : p * (r * (1 + r) ** n) / ((1 + r) ** n - 1);
  // İlk taksitte faiz portion = p*r (en yüksek). Ortalama BSMV yaklaşık: toplam faiz * bsmv / n.
  // Daha doğru: her taksit için faiz hesaplayıp BSMV topla.
  let remaining = p;
  let totalInterest = 0, totalBsmv = 0, totalPayment = 0;
  for (let i = 1; i <= n; i++) {
    const interest = remaining * r;
    let principalPortion = monthlyBase - interest;
    if (i === n) principalPortion = remaining;
    const bsmvPortion = interest * bsmv;
    totalInterest += interest;
    totalBsmv += bsmvPortion;
    totalPayment += (i === n ? principalPortion + interest : monthlyBase) + bsmvPortion;
    remaining -= principalPortion;
  }
  // Aylık taksit tutarı (ilk ayın örneği — sonraki aylarda BSMV azalır)
  const firstBsmv = (p * r) * bsmv;
  const monthly = monthlyBase + firstBsmv;
  return {
    monthly: Math.round(monthly * 100) / 100,
    monthlyBase: Math.round(monthlyBase * 100) / 100,
    total: Math.round(totalPayment * 100) / 100,
    interest: Math.round(totalInterest * 100) / 100,
    bsmv: Math.round(totalBsmv * 100) / 100,
  };
}

export default function Loans() {
  const [loans, setLoans] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [summary, setSummary] = useState(null);
  const [open, setOpen] = useState(false);
  const [selectedId, setSelectedId] = useState(null);
  const [bankFilter, setBankFilter] = useState(null); // null → tüm bankalar

  const load = useCallback(async () => {
    const [{ data: ls }, { data: ac }, { data: sm }] = await Promise.all([
      api.get("/loans"),
      api.get("/accounts"),
      api.get("/loans/summary"),
    ]);
    setLoans(ls); setAccounts(ac); setSummary(sm);
  }, []);
  useEffect(() => { load(); }, [load]);

  // Banka bazlı özet — client-side aggregation
  const bankSummary = useMemo(() => {
    const map = new Map();
    for (const ln of loans) {
      const key = (ln.bank_name || "— Tanımsız —").trim();
      const entry = map.get(key) || {
        bank_name: key,
        count: 0,
        active_count: 0,
        closed_count: 0,
        principal: 0,
        remaining_principal: 0,
        monthly_payment: 0,
        total_payment: 0,
        total_interest: 0,
        total_bsmv: 0,
        next_due_date: null,
      };
      entry.count += 1;
      if (ln.status === "closed") entry.closed_count += 1;
      else entry.active_count += 1;
      entry.principal += Number(ln.principal) || 0;
      entry.remaining_principal += Number(ln.remaining_principal) || 0;
      entry.monthly_payment += Number(ln.monthly_payment) || 0;
      entry.total_payment += Number(ln.total_payment) || 0;
      entry.total_interest += Number(ln.total_interest) || 0;
      entry.total_bsmv += Number(ln.total_bsmv) || 0;
      const nd = ln.next_installment?.due_date;
      if (nd && (!entry.next_due_date || nd < entry.next_due_date)) {
        entry.next_due_date = nd;
      }
      map.set(key, entry);
    }
    return Array.from(map.values()).sort((a, b) =>
      b.remaining_principal - a.remaining_principal
    );
  }, [loans]);

  const filteredLoans = useMemo(() => {
    if (!bankFilter) return loans;
    return loans.filter((ln) => (ln.bank_name || "— Tanımsız —").trim() === bankFilter);
  }, [loans, bankFilter]);

  const activeBankSummary = useMemo(() => {
    if (!bankFilter) return null;
    return bankSummary.find((b) => b.bank_name === bankFilter) || null;
  }, [bankFilter, bankSummary]);

  if (selectedId) {
    return (
      <LoanDetail
        loanId={selectedId}
        accounts={accounts}
        onBack={() => { setSelectedId(null); load(); }}
      />
    );
  }

  return (
    <div className="space-y-6">
      <header className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <p className="label-caps">Kredi & Taksit Takibi</p>
          <h1 className="font-display text-4xl font-bold tracking-tight mt-2">Krediler</h1>
        </div>
        <button
          data-testid="loan-add-button"
          onClick={() => setOpen(true)}
          className="brutal-btn bg-black text-white px-4 py-2.5 text-sm flex items-center gap-2"
        >
          <Plus size={16} /> Yeni Kredi
        </button>
      </header>

      {summary && (
        <div className="grid grid-cols-2 xl:grid-cols-4 gap-3">
          <Stat label="Aktif Kredi" tid="stat-loan-active" plainValue={`${summary.active_loan_count}`} />
          <Stat label="Toplam Anapara Borç" tid="stat-loan-debt" value={summary.total_debt} tone="rose" />
          <Stat label="Bu Ay Ödenecek" tid="stat-loan-month" value={summary.monthly_obligation} tone="black" />
          <Stat label="Gecikmiş Taksit" tid="stat-loan-overdue" value={summary.overdue_amount}
                tone={summary.overdue_count > 0 ? "rose" : "black"}
                subtitle={`${summary.overdue_count} taksit`} icon={AlertTriangle} />
        </div>
      )}

      {summary?.buckets && (
        <div className="brutal-card overflow-hidden" data-testid="loan-buckets-card">
          <div className="p-5 border-b border-black/10 flex items-center justify-between">
            <div>
              <div className="label-caps">Vade Dilimlerine Göre Ödeme Özeti</div>
              <div className="text-xs text-neutral-500 mt-1">Bugünden itibaren yaklaşan kredi taksitleri (gecikmişler hariç)</div>
            </div>
          </div>
          <table className="w-full text-sm" data-testid="loan-buckets-table">
            <thead>
              <tr className="border-b border-black/10 bg-neutral-50">
                <th className="text-left px-5 py-2.5 label-caps">Vade Dilimi</th>
                <th className="text-right px-5 py-2.5 label-caps">Taksit Sayısı</th>
                <th className="text-right px-5 py-2.5 label-caps">Ana Para</th>
                <th className="text-right px-5 py-2.5 label-caps">Toplam Tutar</th>
                <th className="text-right px-5 py-2.5 label-caps">Pay (%)</th>
              </tr>
            </thead>
            <tbody>
              {(() => {
                const total = summary.buckets.reduce((s, b) => s + (b.amount || 0), 0);
                return summary.buckets.map((b) => {
                  const pct = total > 0 ? Math.round((b.amount / total) * 1000) / 10 : 0;
                  return (
                    <tr key={b.key} className="border-b border-black/5 hover:bg-neutral-50" data-testid={`loan-bucket-${b.key}`}>
                      <td className="px-5 py-3 font-medium">{b.label}</td>
                      <td className="px-5 py-3 text-right font-mono-num">{b.count}</td>
                      <td className={`px-5 py-3 text-right font-mono-num ${(b.principal || 0) > 0 ? "text-neutral-700" : "text-neutral-400"}`} data-testid={`loan-bucket-principal-${b.key}`}>
                        {fmtTRY(b.principal || 0)}
                      </td>
                      <td className={`px-5 py-3 text-right font-mono-num font-semibold ${b.amount > 0 ? "" : "text-neutral-400"}`}>
                        {fmtTRY(b.amount)}
                      </td>
                      <td className="px-5 py-3 text-right font-mono-num text-neutral-600">
                        {b.amount > 0 ? `%${pct}` : "—"}
                      </td>
                    </tr>
                  );
                });
              })()}
              <tr className="border-t-2 border-black/80 bg-neutral-50 font-semibold">
                <td className="px-5 py-3 uppercase text-xs tracking-widest">Toplam</td>
                <td className="px-5 py-3 text-right font-mono-num">
                  {summary.buckets.reduce((s, b) => s + b.count, 0)}
                </td>
                <td className="px-5 py-3 text-right font-mono-num" data-testid="loan-buckets-principal-total">
                  {fmtTRY(summary.buckets.reduce((s, b) => s + (b.principal || 0), 0))}
                </td>
                <td className="px-5 py-3 text-right font-mono-num" data-testid="loan-buckets-total">
                  {fmtTRY(summary.buckets.reduce((s, b) => s + b.amount, 0))}
                </td>
                <td className="px-5 py-3 text-right font-mono-num">%100</td>
              </tr>
            </tbody>
          </table>
        </div>
      )}

      {/* Banka Bazlı Özet — tıklanırsa o bankanın kredilerine filtre uygular */}
      {bankSummary.length > 0 && !bankFilter && (
        <div className="brutal-card overflow-hidden" data-testid="bank-summary-card">
          <div className="p-5 border-b border-black/10 flex items-center justify-between">
            <div>
              <div className="label-caps flex items-center gap-2">
                <Landmark size={13} /> Banka Bazlı Özet
              </div>
              <div className="text-xs text-neutral-500 mt-1">
                Bir bankaya tıklayın — o bankadaki tüm kredilerin konsolide özeti ve detayları gelsin
              </div>
            </div>
            <div className="text-[11px] uppercase tracking-widest text-neutral-500 font-mono-num">
              {bankSummary.length} banka
            </div>
          </div>
          <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="bank-summary-table">
            <thead>
              <tr className="border-b border-black/10 bg-neutral-50">
                <th className="text-left px-5 py-2.5 label-caps">Banka</th>
                <th className="text-right px-5 py-2.5 label-caps">Kredi Sayısı</th>
                <th className="text-right px-5 py-2.5 label-caps">Toplam Anapara</th>
                <th className="text-right px-5 py-2.5 label-caps">Kalan Anapara</th>
                <th className="text-right px-5 py-2.5 label-caps">Aylık Yük</th>
                <th className="text-right px-5 py-2.5 label-caps">Toplam Faiz</th>
                <th className="text-right px-5 py-2.5 label-caps">Sıradaki Vade</th>
              </tr>
            </thead>
            <tbody>
              {bankSummary.map((b) => (
                <tr
                  key={b.bank_name}
                  onClick={() => setBankFilter(b.bank_name)}
                  data-testid={`bank-row-${b.bank_name}`}
                  className="border-b border-black/5 hover:bg-amber-50 cursor-pointer transition-colors"
                >
                  <td className="px-5 py-3 font-semibold">
                    <div className="flex items-center gap-2">
                      <span>{b.bank_name}</span>
                      <ChevronRight size={14} className="text-neutral-400" />
                    </div>
                  </td>
                  <td className="px-5 py-3 text-right font-mono-num">
                    {b.count}
                    {b.closed_count > 0 && (
                      <span className="text-[10px] text-emerald-700 ml-1">({b.closed_count} kapalı)</span>
                    )}
                  </td>
                  <td className="px-5 py-3 text-right font-mono-num">{fmtTRY(b.principal)}</td>
                  <td className="px-5 py-3 text-right font-mono-num font-semibold text-rose-600">{fmtTRY(b.remaining_principal)}</td>
                  <td className="px-5 py-3 text-right font-mono-num">{fmtTRY(b.monthly_payment)}</td>
                  <td className="px-5 py-3 text-right font-mono-num text-neutral-600">{fmtTRY(b.total_interest)}</td>
                  <td className="px-5 py-3 text-right font-mono-num text-xs text-neutral-600">
                    {b.next_due_date ? fmtDate(b.next_due_date) : "—"}
                  </td>
                </tr>
              ))}
              <tr className="border-t-2 border-black/80 bg-neutral-50 font-semibold">
                <td className="px-5 py-3 uppercase text-xs tracking-widest">Toplam</td>
                <td className="px-5 py-3 text-right font-mono-num">
                  {bankSummary.reduce((s, b) => s + b.count, 0)}
                </td>
                <td className="px-5 py-3 text-right font-mono-num" data-testid="bank-summary-principal-total">
                  {fmtTRY(bankSummary.reduce((s, b) => s + b.principal, 0))}
                </td>
                <td className="px-5 py-3 text-right font-mono-num text-rose-700" data-testid="bank-summary-remaining-total">
                  {fmtTRY(bankSummary.reduce((s, b) => s + b.remaining_principal, 0))}
                </td>
                <td className="px-5 py-3 text-right font-mono-num" data-testid="bank-summary-monthly-total">
                  {fmtTRY(bankSummary.reduce((s, b) => s + b.monthly_payment, 0))}
                </td>
                <td className="px-5 py-3 text-right font-mono-num">
                  {fmtTRY(bankSummary.reduce((s, b) => s + b.total_interest, 0))}
                </td>
                <td className="px-5 py-3 text-right font-mono-num">—</td>
              </tr>
            </tbody>
          </table>
          </div>
        </div>
      )}

      {/* Seçilen banka için konsolide özet */}
      {bankFilter && activeBankSummary && (
        <div className="brutal-card p-6 bg-black text-white" data-testid="bank-detail-card">
          <div className="flex items-center justify-between mb-5">
            <div className="flex items-center gap-3">
              <button
                data-testid="bank-filter-back"
                onClick={() => setBankFilter(null)}
                className="p-2 border border-white/30 hover:bg-white/10"
                title="Tüm bankalara dön"
              >
                <ArrowLeft size={14} />
              </button>
              <div>
                <div className="text-[10px] uppercase tracking-widest text-white/60">Banka Konsolide</div>
                <h2 className="font-display text-2xl font-bold mt-0.5">{activeBankSummary.bank_name}</h2>
              </div>
            </div>
            <div className="text-right">
              <div className="text-[10px] uppercase tracking-widest text-white/60">Kredi Sayısı</div>
              <div className="font-mono-num text-2xl font-bold mt-0.5">
                {activeBankSummary.active_count}
                {activeBankSummary.closed_count > 0 && (
                  <span className="text-sm text-emerald-400 ml-1">+ {activeBankSummary.closed_count} kapalı</span>
                )}
              </div>
            </div>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-5">
            <div>
              <div className="text-[10px] uppercase tracking-widest text-white/60 mb-1">Toplam Anapara</div>
              <div className="font-mono-num text-xl font-bold">{fmtTRY(activeBankSummary.principal)}</div>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-widest text-white/60 mb-1">Kalan Anapara</div>
              <div className="font-mono-num text-xl font-bold text-rose-400">{fmtTRY(activeBankSummary.remaining_principal)}</div>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-widest text-white/60 mb-1">Aylık Yük</div>
              <div className="font-mono-num text-xl font-bold">{fmtTRY(activeBankSummary.monthly_payment)}</div>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-widest text-white/60 mb-1">Toplam Ödenecek</div>
              <div className="font-mono-num text-xl font-bold">{fmtTRY(activeBankSummary.total_payment)}</div>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-widest text-white/60 mb-1">Toplam Faiz</div>
              <div className="font-mono-num text-xl font-bold text-amber-300">{fmtTRY(activeBankSummary.total_interest)}</div>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-widest text-white/60 mb-1">Toplam BSMV</div>
              <div className="font-mono-num text-xl font-bold text-amber-300">{fmtTRY(activeBankSummary.total_bsmv)}</div>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-widest text-white/60 mb-1">Sıradaki Vade</div>
              <div className="font-mono-num text-xl font-bold">
                {activeBankSummary.next_due_date ? fmtDate(activeBankSummary.next_due_date) : "—"}
              </div>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-widest text-white/60 mb-1">Ödeme Oranı</div>
              <div className="font-mono-num text-xl font-bold text-emerald-400">
                {activeBankSummary.principal > 0
                  ? `%${Math.round((1 - activeBankSummary.remaining_principal / activeBankSummary.principal) * 100)}`
                  : "—"}
              </div>
            </div>
          </div>
          <div className="mt-5 pt-4 border-t border-white/15 text-xs text-white/70">
            Aşağıda <b className="text-white">{activeBankSummary.bank_name}</b> bankasındaki {filteredLoans.length} kredinin detayı listelenir. Herhangi birine tıklayın — tüm taksit planını görür ve düzenleyebilirsiniz.
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
        {filteredLoans.length === 0 && (
          <div className="brutal-card p-10 text-center col-span-full text-neutral-500 text-sm">
            {bankFilter
              ? `${bankFilter} bankasında kredi bulunmuyor.`
              : `Henüz kredi eklenmedi. "Yeni Kredi" ile başlayın.`}
          </div>
        )}
        {filteredLoans.map((ln) => (
          <button
            key={ln.id}
            data-testid={`loan-card-${ln.id}`}
            onClick={() => setSelectedId(ln.id)}
            className="brutal-card p-6 text-left hover:cursor-pointer"
          >
            <div className="flex items-start justify-between">
              <div className="flex-1 min-w-0">
                <div className="label-caps">{LOAN_TYPES[ln.loan_type] || "Kredi"} · {ln.bank_name}</div>
                <h3 className="font-display text-xl font-bold mt-1 truncate">{ln.name}</h3>
              </div>
              <ChevronRight size={18} className="text-neutral-400" />
            </div>

            <div className="mt-5 grid grid-cols-2 gap-4">
              <div>
                <div className="label-caps">Anapara</div>
                <div className="font-mono-num text-lg font-semibold mt-0.5">{fmtTRY(ln.principal)}</div>
              </div>
              <div>
                <div className="label-caps">Aylık Taksit</div>
                <div className="font-mono-num text-lg font-semibold mt-0.5">{fmtTRY(ln.monthly_payment)}</div>
              </div>
              <div>
                <div className="label-caps">Kalan Anapara</div>
                <div className="font-mono-num text-lg font-semibold mt-0.5 text-rose-600">{fmtTRY(ln.remaining_principal)}</div>
              </div>
              <div>
                <div className="label-caps">Faiz</div>
                <div className="font-mono-num text-lg font-semibold mt-0.5">%{ln.interest_rate}</div>
              </div>
            </div>

            <div className="mt-5">
              <div className="flex items-center justify-between text-xs text-neutral-600">
                <span>{ln.paid_count} / {ln.total_count} taksit ödendi</span>
                <span>%{ln.progress_percent}</span>
              </div>
              <div className="mt-2 h-2 bg-neutral-100 border border-black/10">
                <div
                  className={`h-full ${ln.status === "closed" ? "bg-emerald-600" : "bg-black"}`}
                  style={{ width: `${ln.progress_percent}%` }}
                />
              </div>
            </div>

            {ln.next_installment && (
              <div className="mt-4 pt-4 border-t border-black/10 flex items-center justify-between">
                <div>
                  <div className="label-caps">Sıradaki Taksit</div>
                  <div className="text-sm font-medium mt-0.5">
                    {ln.next_installment.installment_no}. taksit · {fmtDate(ln.next_installment.due_date)}
                  </div>
                </div>
                <div className={`font-mono-num font-semibold ${ln.next_installment.status === "overdue" ? "text-rose-600" : ""}`}>
                  {fmtTRY(ln.next_installment.amount)}
                </div>
              </div>
            )}
            {ln.status === "closed" && (
              <div className="mt-4 pt-4 border-t border-black/10 flex items-center gap-2 text-emerald-700 text-sm">
                <CheckCircle2 size={15} /> Kredi kapatıldı
              </div>
            )}
          </button>
        ))}
      </div>

      {open && (
        <LoanCreateModal
          accounts={accounts}
          onClose={() => setOpen(false)}
          onSaved={() => { setOpen(false); load(); }}
        />
      )}
    </div>
  );
}

function Stat({ label, value, plainValue, tone, tid, icon: Icon, subtitle }) {
  const color = tone === "rose" ? "text-rose-600" : tone === "emerald" ? "text-emerald-700" : "";
  return (
    <div className="brutal-card p-5" data-testid={tid}>
      <div className="flex items-start justify-between">
        <span className="label-caps">{label}</span>
        {Icon && <Icon size={16} className="text-neutral-500" />}
      </div>
      <div className={`font-mono-num text-2xl md:text-3xl font-semibold mt-3 ${color}`}>
        {plainValue !== undefined ? plainValue : fmtTRY(value)}
      </div>
      {subtitle && <div className="text-[11px] text-neutral-500 mt-1">{subtitle}</div>}
    </div>
  );
}

// ================= Detail =================

function LoanDetail({ loanId, accounts, onBack }) {
  const [loan, setLoan] = useState(null);
  const [payingId, setPayingId] = useState(null);
  const [selectedAccount, setSelectedAccount] = useState("");
  const [editingId, setEditingId] = useState(null);
  const [editData, setEditData] = useState({ amount: "", due_date: "", principal_portion: "", interest_portion: "", bsmv_portion: "", remaining_principal: "" });

  const load = useCallback(async () => {
    const { data } = await api.get(`/loans/${loanId}`);
    setLoan(data);
    setSelectedAccount(data.account_id || "");
  }, [loanId]);
  useEffect(() => { load(); }, [load]);

  const startEdit = (inst) => {
    setEditingId(inst.id);
    setEditData({
      amount: String(inst.amount ?? ""),
      due_date: inst.due_date || "",
      principal_portion: String(inst.principal_portion ?? ""),
      interest_portion: String(inst.interest_portion ?? ""),
      bsmv_portion: String(inst.bsmv_portion ?? ""),
      remaining_principal: String(inst.remaining_principal ?? ""),
    });
  };
  const cancelEdit = () => { setEditingId(null); setEditData({ amount: "", due_date: "", principal_portion: "", interest_portion: "", bsmv_portion: "", remaining_principal: "" }); };
  const saveEdit = async (inst) => {
    const amt = Number(editData.amount);
    if (!(amt > 0)) { toast.error("Tutar 0'dan büyük olmalı"); return; }
    const toNum = (v) => (v === "" || v === null || v === undefined) ? 0 : Number(v);
    try {
      await api.patch(`/loans/${loan.id}/installments/${inst.id}`, {
        amount: amt,
        due_date: editData.due_date,
        principal_portion: toNum(editData.principal_portion),
        interest_portion: toNum(editData.interest_portion),
        bsmv_portion: toNum(editData.bsmv_portion),
        remaining_principal: toNum(editData.remaining_principal),
      });
      toast.success("Taksit güncellendi");
      cancelEdit();
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Kaydedilemedi");
    }
  };

  const pay = async (inst) => {
    const accId = selectedAccount || loan.account_id;
    if (!accId) { toast.error("Ödeme yapılacak hesabı seçin"); return; }
    setPayingId(inst.id);
    try {
      const res = await api.post(`/loans/${loan.id}/installments/${inst.id}/pay`, { account_id: accId });
      toast.success(res.data.loan_closed ? "Taksit ödendi · Kredi kapatıldı 🎉" : "Taksit ödendi");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Hata");
    } finally {
      setPayingId(null);
    }
  };

  const undoPay = async (inst) => {
    if (!window.confirm("Taksit ödemesi geri alınsın mı? İlgili gider kaydı da silinecek.")) return;
    await api.post(`/loans/${loan.id}/installments/${inst.id}/undo-pay`);
    toast.success("Ödeme geri alındı");
    load();
  };

  const remove = async () => {
    if (!window.confirm("Kredi ve taksit planı silinsin mi?")) return;
    await api.delete(`/loans/${loan.id}`);
    toast.success("Silindi");
    onBack();
  };

  if (!loan) return <div className="label-caps">Yükleniyor…</div>;

  const paid = loan.installments.filter((i) => i.status === "paid");
  const totalPaid = paid.reduce((s, i) => s + i.amount, 0);
  const remainingBalance = loan.installments.filter((i) => i.status !== "paid").reduce((s, i) => s + i.principal_portion, 0);

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3">
        <button
          data-testid="loan-back-button"
          onClick={onBack}
          className="p-2 hover:bg-neutral-100 border border-black/10"
        >
          <ArrowLeft size={16} />
        </button>
        <div className="flex-1">
          <div className="label-caps">{LOAN_TYPES[loan.loan_type]} · {loan.bank_name}</div>
          <h1 className="font-display text-3xl font-bold tracking-tight mt-1">{loan.name}</h1>
        </div>
        <button
          data-testid="loan-delete-button"
          onClick={remove}
          className="p-2 border border-rose-300 text-rose-600 hover:bg-rose-50"
        >
          <Trash2 size={16} />
        </button>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Info label="Anapara" value={fmtTRY(loan.principal)} />
        {loan.schedule_mode !== "fixed" && (
          <Info label="Yıllık Faiz" value={`%${loan.interest_rate}`} />
        )}
        {loan.schedule_mode !== "fixed" && (
          <Info label="BSMV" value={`%${loan.bsmv_rate || 0}`} />
        )}
        {loan.schedule_mode === "fixed" && (
          <Info label="Plan Modu" value="Banka Planı (Sabit)" />
        )}
        <Info label="Vade" value={`${loan.term_months} ay`} />
        <Info label={loan.schedule_mode === "fixed" ? "Sabit Aylık Taksit" : "Aylık Taksit"} value={fmtTRY(loan.monthly_payment)} />
        <Info label="Toplam Ödenecek" value={fmtTRY(loan.total_payment)} />
        {loan.schedule_mode !== "fixed" && (
          <Info label="Toplam Faiz" value={fmtTRY(loan.total_interest)} tone="rose" />
        )}
        {loan.schedule_mode !== "fixed" && (loan.total_bsmv || 0) > 0 && (
          <Info label="Toplam BSMV" value={fmtTRY(loan.total_bsmv)} tone="rose" />
        )}
        <Info label="Ödenen" value={fmtTRY(totalPaid)} tone="emerald" />
        <Info label="Kalan Anapara" value={fmtTRY(remainingBalance)} tone="rose" />
      </div>

      <div className="brutal-card p-4 flex flex-wrap items-center gap-3">
        <div className="label-caps">Ödeme Hesabı</div>
        <select
          data-testid="loan-detail-account"
          value={selectedAccount}
          onChange={(e) => setSelectedAccount(e.target.value)}
          className="border border-black/20 px-3 py-1.5 bg-white outline-none text-sm min-w-[200px]"
        >
          <option value="">— Seçin —</option>
          {accounts.map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}
        </select>
        <div className="text-[11px] text-neutral-500 ml-auto">
          Taksit ödendiğinde bu hesaptan otomatik gider oluşturulur.
        </div>
      </div>

      <div className="brutal-card overflow-x-auto">
        <table className="w-full text-sm" data-testid="loan-schedule-table">
          <thead>
            <tr className="border-b-2 border-black/80">
              <th className="text-left px-4 py-3 label-caps">#</th>
              <th className="text-left px-4 py-3 label-caps">Vade</th>
              <th className="text-right px-4 py-3 label-caps">Taksit</th>
              <th className="text-right px-4 py-3 label-caps">Anapara</th>
              <th className="text-right px-4 py-3 label-caps">Faiz</th>
              <th className="text-right px-4 py-3 label-caps">BSMV</th>
              <th className="text-right px-4 py-3 label-caps">Kalan</th>
              <th className="text-left px-4 py-3 label-caps">Durum</th>
              <th className="px-4 py-3"></th>
            </tr>
          </thead>
          <tbody>
            {loan.installments.map((i) => {
              const isEditing = editingId === i.id;
              const canEdit = i.status !== "paid";
              return (
              <tr key={i.id} className={`border-b border-black/5 hover:bg-neutral-50 ${isEditing ? "bg-amber-50" : ""}`}>
                <td className="px-4 py-3 font-mono-num text-neutral-600">{i.installment_no}</td>
                <td className="px-4 py-3 font-mono-num">
                  {isEditing ? (
                    <input
                      type="date"
                      data-testid={`installment-due-input-${i.installment_no}`}
                      value={editData.due_date}
                      onChange={(e) => setEditData({ ...editData, due_date: e.target.value })}
                      className="input text-xs py-1 w-36 bg-white"
                    />
                  ) : (
                    fmtDate(i.due_date)
                  )}
                </td>
                <td className="px-4 py-3 text-right font-mono-num font-semibold">
                  {isEditing ? (
                    <input
                      type="number" step="0.01" min="0.01"
                      data-testid={`installment-amount-input-${i.installment_no}`}
                      value={editData.amount}
                      onChange={(e) => setEditData({ ...editData, amount: e.target.value })}
                      className="input text-right text-xs py-1 w-28 font-mono-num bg-white"
                    />
                  ) : (
                    fmtTRY(i.amount)
                  )}
                </td>
                <td className="px-4 py-3 text-right font-mono-num">
                  {isEditing ? (
                    <input
                      type="number" step="0.01" min="0"
                      data-testid={`installment-principal-input-${i.installment_no}`}
                      value={editData.principal_portion}
                      onChange={(e) => setEditData({ ...editData, principal_portion: e.target.value })}
                      className="input text-right text-xs py-1 w-24 font-mono-num bg-white"
                    />
                  ) : (
                    fmtTRY(i.principal_portion)
                  )}
                </td>
                <td className="px-4 py-3 text-right font-mono-num text-neutral-500">
                  {isEditing ? (
                    <input
                      type="number" step="0.01" min="0"
                      data-testid={`installment-interest-input-${i.installment_no}`}
                      value={editData.interest_portion}
                      onChange={(e) => setEditData({ ...editData, interest_portion: e.target.value })}
                      className="input text-right text-xs py-1 w-24 font-mono-num bg-white"
                    />
                  ) : (
                    fmtTRY(i.interest_portion)
                  )}
                </td>
                <td
                  data-testid={`installment-bsmv-${i.installment_no}`}
                  className={`px-4 py-3 text-right font-mono-num ${(i.bsmv_portion || 0) > 0 ? "text-amber-800" : "text-neutral-400"}`}
                >
                  {isEditing ? (
                    <input
                      type="number" step="0.01" min="0"
                      data-testid={`installment-bsmv-input-${i.installment_no}`}
                      value={editData.bsmv_portion}
                      onChange={(e) => setEditData({ ...editData, bsmv_portion: e.target.value })}
                      className="input text-right text-xs py-1 w-20 font-mono-num bg-white"
                    />
                  ) : (
                    (i.bsmv_portion || 0) > 0 ? fmtTRY(i.bsmv_portion) : "—"
                  )}
                </td>
                <td className="px-4 py-3 text-right font-mono-num text-neutral-500">
                  {isEditing ? (
                    <input
                      type="number" step="0.01" min="0"
                      data-testid={`installment-remaining-input-${i.installment_no}`}
                      value={editData.remaining_principal}
                      onChange={(e) => setEditData({ ...editData, remaining_principal: e.target.value })}
                      className="input text-right text-xs py-1 w-24 font-mono-num bg-white"
                    />
                  ) : (
                    fmtTRY(i.remaining_principal)
                  )}
                </td>
                <td className="px-4 py-3">
                  <span className={`border px-2 py-0.5 text-[11px] uppercase tracking-widest ${STATUS_COLOR[i.status]}`}>
                    {i.status === "paid" ? "Ödendi" : i.status === "overdue" ? "Gecikti" : "Bekliyor"}
                  </span>
                  {i.paid_date && <div className="text-[10px] text-neutral-400 mt-1">Ödeme: {fmtDate(i.paid_date)}</div>}
                </td>
                <td className="px-4 py-3 text-right whitespace-nowrap">
                  {isEditing ? (
                    <>
                      <button
                        type="button"
                        data-testid={`installment-save-${i.installment_no}`}
                        onClick={() => saveEdit(i)}
                        className="p-1.5 border border-emerald-600 text-emerald-700 hover:bg-emerald-50 mr-1"
                        title="Kaydet"
                      >
                        <Check size={14} />
                      </button>
                      <button
                        type="button"
                        data-testid={`installment-cancel-${i.installment_no}`}
                        onClick={cancelEdit}
                        className="p-1.5 border border-black/15 hover:bg-neutral-100"
                        title="İptal"
                      >
                        <X size={14} />
                      </button>
                    </>
                  ) : (
                    <>
                      {canEdit && (
                        <button
                          type="button"
                          data-testid={`installment-edit-${i.installment_no}`}
                          onClick={() => startEdit(i)}
                          className="p-1.5 hover:bg-neutral-100 border border-transparent hover:border-black/10 mr-1"
                          title="Düzenle"
                        >
                          <Edit3 size={13} />
                        </button>
                      )}
                      {i.status !== "paid" ? (
                        <button
                          data-testid={`installment-pay-${i.installment_no}`}
                          disabled={payingId === i.id}
                          onClick={() => pay(i)}
                          className="brutal-btn bg-black text-white px-3 py-1.5 text-xs disabled:opacity-40"
                        >
                          Öde
                        </button>
                      ) : (
                        <button
                          data-testid={`installment-undo-${i.installment_no}`}
                          onClick={() => undoPay(i)}
                          className="p-1.5 hover:bg-neutral-100"
                          title="Ödemeyi geri al"
                        >
                          <Undo2 size={14} />
                        </button>
                      )}
                    </>
                  )}
                </td>
              </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function Info({ label, value, tone }) {
  const color = tone === "rose" ? "text-rose-600" : tone === "emerald" ? "text-emerald-700" : "";
  return (
    <div className="brutal-card p-4">
      <div className="label-caps">{label}</div>
      <div className={`font-mono-num text-lg font-semibold mt-1 ${color}`}>{value}</div>
    </div>
  );
}

// ================= Create Modal =================

function LoanCreateModal({ accounts, onClose, onSaved }) {
  const [form, setForm] = useState({
    name: "",
    bank_name: "",
    loan_type: "rotative",
    schedule_mode: "auto",              // "auto" | "fixed" | "import"
    principal: "",
    interest_rate: "",
    bsmv_rate: "",
    fixed_installment_amount: "",
    start_date: todayISO(),
    term_months: 12,
    account_id: accounts[0]?.id || "",
    notes: "",
  });
  const [importedInsts, setImportedInsts] = useState([]);   // {due_date, amount}
  const [importErr, setImportErr] = useState("");
  const [fileName, setFileName] = useState("");

  const isFixed = form.schedule_mode === "fixed";
  const isImport = form.schedule_mode === "import";

  const preview = useMemo(() => {
    if (isImport) {
      const n = importedInsts.length;
      const total = importedInsts.reduce((s, r) => s + r.amount, 0);
      const principal = Number(form.principal) || 0;
      const interest = Math.max(0, total - principal);
      const monthly = n > 0 ? importedInsts[0].amount : 0;
      return { monthly, monthlyBase: monthly, total, interest, bsmv: 0, n };
    }
    if (isFixed) {
      const amt = Number(form.fixed_installment_amount) || 0;
      const n = Number(form.term_months) || 0;
      const total = amt * n;
      const principal = Number(form.principal) || 0;
      const interest = Math.max(0, total - principal);
      return { monthly: amt, monthlyBase: amt, total, interest, bsmv: 0 };
    }
    return annuityPreview(form.principal, form.interest_rate, form.term_months, form.bsmv_rate);
  }, [isImport, isFixed, importedInsts, form.fixed_installment_amount, form.term_months, form.principal, form.interest_rate, form.bsmv_rate]);

  const onFilePick = async (e) => {
    setImportErr("");
    const f = e.target.files?.[0];
    if (!f) return;
    setFileName(f.name);
    try {
      const rows = await parseInstallmentsFile(f);
      if (rows.length === 0) {
        setImportedInsts([]);
        setImportErr("Dosyada geçerli satır bulunamadı. İki sütun (Vade, Tutar) bekleniyor.");
        return;
      }
      setImportedInsts(rows);
      toast.success(`${rows.length} taksit içe aktarıldı`);
    } catch (err) {
      setImportedInsts([]);
      setImportErr("Dosya okunamadı. Excel (.xlsx) veya CSV formatında olmalı.");
    }
  };

  const save = async (e) => {
    e.preventDefault();
    if (isFixed && !(Number(form.fixed_installment_amount) > 0)) {
      toast.error("Sabit taksit tutarı 0'dan büyük olmalı");
      return;
    }
    if (isImport && importedInsts.length === 0) {
      toast.error("Önce bir plan dosyası yükleyin");
      return;
    }
    try {
      const payload = {
        ...form,
        principal: Number(form.principal) || 0,
        term_months: Number(form.term_months),
      };
      if (isImport) {
        payload.installments = importedInsts;
        payload.schedule_mode = "fixed";
        payload.interest_rate = 0;
        payload.bsmv_rate = 0;
        payload.term_months = importedInsts.length;
        delete payload.fixed_installment_amount;
      } else if (isFixed) {
        payload.fixed_installment_amount = Number(form.fixed_installment_amount);
        payload.interest_rate = Number(form.interest_rate) || 0;
        payload.bsmv_rate = 0;
      } else {
        payload.interest_rate = Number(form.interest_rate);
        payload.bsmv_rate = Number(form.bsmv_rate) || 0;
        delete payload.fixed_installment_amount;
      }
      await api.post("/loans", payload);
      toast.success("Kredi ve taksit planı oluşturuldu");
      onSaved();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Kaydedilemedi");
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={onClose}>
      <form
        onClick={(e) => e.stopPropagation()}
        onSubmit={save}
        data-testid="loan-form-modal"
        className="bg-white border border-black w-full max-w-2xl max-h-[92vh] overflow-y-auto"
      >
        <div className="flex items-center justify-between p-5 border-b-2 border-black">
          <h2 className="font-display text-xl font-bold">Yeni Kredi</h2>
          <button type="button" onClick={onClose}><X size={18} /></button>
        </div>

        <div className="p-5 space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <F label="Kredi Adı">
              <input
                required data-testid="loan-name"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                className="input" placeholder="Örn: Ticari Kredi"
              />
            </F>
            <F label="Banka">
              <input
                required data-testid="loan-bank"
                value={form.bank_name}
                onChange={(e) => setForm({ ...form, bank_name: e.target.value })}
                className="input" placeholder="Örn: Garanti BBVA"
              />
            </F>
          </div>

          <F label="Kredi Türü">
            <div className="grid grid-cols-3 gap-1.5">
              {Object.entries(LOAN_TYPES).map(([k, l]) => (
                <button
                  key={k}
                  type="button"
                  data-testid={`loan-type-${k}`}
                  onClick={() => setForm({ ...form, loan_type: k })}
                  className={`text-xs uppercase tracking-widest py-2 border ${
                    form.loan_type === k
                      ? "bg-black text-white border-black"
                      : "border-black/15 hover:border-black/40"
                  }`}
                >
                  {l}
                </button>
              ))}
            </div>
          </F>

          <F label="Ödeme Planı Modu">
            <div className="grid grid-cols-3 gap-1.5">
              <button
                type="button"
                data-testid="loan-mode-auto"
                onClick={() => setForm({ ...form, schedule_mode: "auto" })}
                className={`text-xs uppercase tracking-widest py-2 border ${
                  form.schedule_mode === "auto" ? "bg-black text-white border-black" : "border-black/15 hover:border-black/40"
                }`}
                title="Anapara + faiz + BSMV → annuite ile hesaplanır"
              >Otomatik</button>
              <button
                type="button"
                data-testid="loan-mode-fixed"
                onClick={() => setForm({ ...form, schedule_mode: "fixed" })}
                className={`text-xs uppercase tracking-widest py-2 border ${
                  form.schedule_mode === "fixed" ? "bg-black text-white border-black" : "border-black/15 hover:border-black/40"
                }`}
                title="Banka planından sabit taksit + adet girin"
              >Banka Planı</button>
              <button
                type="button"
                data-testid="loan-mode-import"
                onClick={() => setForm({ ...form, schedule_mode: "import" })}
                className={`text-xs uppercase tracking-widest py-2 border inline-flex items-center justify-center gap-1 ${
                  form.schedule_mode === "import" ? "bg-black text-white border-black" : "border-black/15 hover:border-black/40"
                }`}
                title="Excel/CSV dosyasından geri ödeme planı yükleyin"
              ><Upload size={12} /> İçe Aktar</button>
            </div>
            <p className="text-[11px] text-neutral-500 mt-1.5">
              {isImport
                ? "Bankanızın verdiği geri ödeme planını Excel (.xlsx) veya CSV olarak yükleyin. İki sütun beklenir: Vade Tarihi, Taksit Tutarı."
                : isFixed
                ? "Banka planınızdaki sabit aylık taksit tutarını ve toplam adet sayısını girin."
                : "Anapara + Yıllık Faiz + Vade + BSMV oranı ile annuite yöntemiyle plan üretilir."}
            </p>
          </F>

          {isImport ? (
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <F label="Anapara (₺) — opsiyonel">
                  <input
                    type="number" step="0.01" data-testid="loan-principal"
                    value={form.principal}
                    onChange={(e) => setForm({ ...form, principal: e.target.value })}
                    className="input font-mono-num" placeholder="Bilgi amaçlı"
                  />
                </F>
                <F label="İlk Vade Tarihi (bilgi)">
                  <input
                    type="date" data-testid="loan-start"
                    value={form.start_date}
                    onChange={(e) => setForm({ ...form, start_date: e.target.value })}
                    className="input"
                  />
                </F>
              </div>
              <div className="border border-dashed border-black/25 p-4 bg-neutral-50">
                <label className="flex flex-col items-center justify-center cursor-pointer text-center">
                  <FileSpreadsheet size={28} className="text-neutral-500 mb-2" />
                  <span className="text-sm font-semibold uppercase tracking-widest">
                    {fileName ? "Dosya Yüklendi" : "Excel / CSV Dosyası Yükle"}
                  </span>
                  <span className="text-xs text-neutral-500 mt-1">
                    {fileName || ".xlsx, .xls veya .csv formatında"}
                  </span>
                  <input
                    type="file"
                    accept=".xlsx,.xls,.csv"
                    data-testid="loan-import-file"
                    onChange={onFilePick}
                    className="hidden"
                  />
                </label>
              </div>
              {importErr && <div className="text-xs text-rose-600" data-testid="loan-import-error">{importErr}</div>}
              {importedInsts.length > 0 && (
                <div className="border border-black/10">
                  <div className="flex items-center justify-between px-3 py-2 border-b border-black/10 bg-neutral-50 text-xs uppercase tracking-widest">
                    <span data-testid="loan-import-count">Yüklenen: {importedInsts.length} taksit</span>
                    <span className="font-mono-num">Toplam: {fmtTRY(preview.total)}</span>
                  </div>
                  <div className="max-h-48 overflow-y-auto text-sm">
                    <table className="min-w-full">
                      <thead className="text-[10px] uppercase tracking-widest text-neutral-500 sticky top-0 bg-white">
                        <tr className="border-b border-black/10">
                          <th className="text-left px-3 py-1.5">#</th>
                          <th className="text-left px-3 py-1.5">Vade</th>
                          <th className="text-right px-3 py-1.5">Tutar</th>
                        </tr>
                      </thead>
                      <tbody data-testid="loan-import-preview">
                        {importedInsts.slice(0, 24).map((r, idx) => (
                          <tr key={idx} className="border-b border-black/5">
                            <td className="px-3 py-1 text-neutral-500 font-mono-num">{idx + 1}</td>
                            <td className="px-3 py-1 font-mono-num">{fmtDate(r.due_date)}</td>
                            <td className="px-3 py-1 text-right font-mono-num">{fmtTRY(r.amount)}</td>
                          </tr>
                        ))}
                        {importedInsts.length > 24 && (
                          <tr><td colSpan={3} className="px-3 py-2 text-center text-xs text-neutral-500">
                            ... ve {importedInsts.length - 24} taksit daha
                          </td></tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>
          ) : isFixed ? (
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
              <F label="Anapara (₺) — opsiyonel">
                <input
                  type="number" step="0.01" data-testid="loan-principal"
                  value={form.principal}
                  onChange={(e) => setForm({ ...form, principal: e.target.value })}
                  className="input font-mono-num"
                  placeholder="Bilgi amaçlı"
                />
              </F>
              <F label="Sabit Aylık Taksit (₺) *">
                <input
                  required type="number" step="0.01" min="0.01"
                  data-testid="loan-fixed-installment"
                  value={form.fixed_installment_amount}
                  onChange={(e) => setForm({ ...form, fixed_installment_amount: e.target.value })}
                  className="input font-mono-num"
                  placeholder="Banka planındaki taksit"
                />
              </F>
              <F label="Vade (Ay) *">
                <input
                  required type="number" min="1" max="480" data-testid="loan-term"
                  value={form.term_months}
                  onChange={(e) => setForm({ ...form, term_months: e.target.value })}
                  className="input font-mono-num"
                />
              </F>
            </div>
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <F label="Anapara (₺)">
                <input
                  required type="number" step="0.01" data-testid="loan-principal"
                  value={form.principal}
                  onChange={(e) => setForm({ ...form, principal: e.target.value })}
                  className="input font-mono-num"
                />
              </F>
              <F label="Yıllık Faiz (%)">
                <input
                  required type="number" step="0.01" data-testid="loan-rate"
                  value={form.interest_rate}
                  onChange={(e) => setForm({ ...form, interest_rate: e.target.value })}
                  className="input font-mono-num" placeholder="45"
                />
              </F>
              <F label="BSMV (%)">
                <input
                  type="number" step="0.01" min="0" max="100" data-testid="loan-bsmv"
                  value={form.bsmv_rate}
                  onChange={(e) => setForm({ ...form, bsmv_rate: e.target.value })}
                  className="input font-mono-num" placeholder="10"
                  title="Banka ve Sigorta Muameleleri Vergisi — faiz üzerinden alınır"
                />
              </F>
              <F label="Vade (Ay)">
                <input
                  required type="number" min="1" max="480" data-testid="loan-term"
                  value={form.term_months}
                  onChange={(e) => setForm({ ...form, term_months: e.target.value })}
                  className="input font-mono-num"
                />
              </F>
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <F label="Kullanım Tarihi">
              <input
                required type="date" data-testid="loan-start-date"
                value={form.start_date}
                onChange={(e) => setForm({ ...form, start_date: e.target.value })}
                className="input"
              />
            </F>
            <F label="Ödeme Hesabı (Opsiyonel)">
              <select
                data-testid="loan-account"
                value={form.account_id}
                onChange={(e) => setForm({ ...form, account_id: e.target.value })}
                className="input bg-white"
              >
                <option value="">—</option>
                {accounts.map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}
              </select>
            </F>
          </div>

          <F label="Notlar">
            <textarea
              rows={2}
              value={form.notes}
              onChange={(e) => setForm({ ...form, notes: e.target.value })}
              className="input"
            />
          </F>

          <div className="border-2 border-black bg-neutral-50 p-4" data-testid="loan-preview">
            <div className="label-caps mb-3 flex items-center gap-2">
              <CircleDollarSign size={13} /> Ödeme Planı Önizlemesi
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <PreviewRow label={isImport ? "İlk Taksit" : isFixed ? "Sabit Aylık Taksit" : "İlk Ay Taksit"} value={fmtTRY(preview.monthly)} tid="preview-monthly" />
              <PreviewRow label="Toplam Ödenecek" value={fmtTRY(preview.total)} tid="preview-total" />
              {!isFixed && !isImport && (
                <PreviewRow label="Toplam Faiz" value={fmtTRY(preview.interest)} tone="rose" tid="preview-interest" />
              )}
              {!isFixed && !isImport && (
                <PreviewRow label="Toplam BSMV" value={fmtTRY(preview.bsmv)} tone={preview.bsmv > 0 ? "rose" : "neutral"} tid="preview-bsmv" />
              )}
              {isImport && (
                <PreviewRow label="Taksit Adedi" value={`${importedInsts.length}`} tid="preview-import-count" />
              )}
              {isFixed && (
                <PreviewRow label="Adet × Taksit" value={`${form.term_months || 0} × ${fmtTRY(Number(form.fixed_installment_amount) || 0)}`} tid="preview-fixed-note" />
              )}
            </div>
            <div className="text-[11px] text-neutral-500 mt-2">
              {isImport
                ? `İçe aktarılan plan: ${importedInsts.length} taksit. Anapara/faiz/BSMV ayrımı yapılmaz.`
                : isFixed
                ? `Banka planı modu: Toplam tutar = Sabit Taksit × ${form.term_months || 0} ay. Anapara/faiz/BSMV ayrımı yapılmaz.`
                : "Annuite (sabit taksit) + BSMV yöntemiyle hesaplandı. BSMV faiz üzerinden alındığı için aylık BSMV taksit süresi boyunca azalır."}
            </div>
          </div>
        </div>

        <div className="flex justify-end gap-2 p-5 border-t border-black/10 bg-white">
          <button type="button" onClick={onClose} className="border border-black/20 px-4 py-2 text-sm">
            Vazgeç
          </button>
          <button
            data-testid="loan-save-button" type="submit"
            className="brutal-btn bg-black text-white px-5 py-2 text-sm"
          >
            Kredi Oluştur
          </button>
        </div>

        <style>{`.input{width:100%;border:1px solid rgba(0,0,0,0.2);padding:0.5rem 0.75rem;outline:none}`}</style>
      </form>
    </div>
  );
}

function PreviewRow({ label, value, tone, tid }) {
  const color = tone === "rose" ? "text-rose-600" : "";
  return (
    <div data-testid={tid}>
      <div className="label-caps text-[10px]">{label}</div>
      <div className={`font-mono-num text-lg font-semibold mt-0.5 ${color}`}>{value}</div>
    </div>
  );
}

function F({ label, children }) {
  return <label className="block"><div className="label-caps mb-1.5">{label}</div>{children}</label>;
}
